import TixStripeError from '@/checkout/stripe/TixStripeError'
import ValidationError from '@/errors/ValidationError'
import { config } from '@/helpers/Environment'
import type { CreatePaymentMethodData, PaymentMethod, Stripe } from '@stripe/stripe-js'

/**
 * Creates a payment-intent/payment-method to prepare for payment.
 *
 * @see https://stripe.com/docs/payments/payment-intents/migration-synchronous
 */
export function authorizeCreditCard(stripe: Stripe, element, identityFields: Dictionary): Promise<PaymentMethod> {
  const options: CreatePaymentMethodData = {
    type: 'card',
    card: element,
    billing_details: billingDetails(identityFields),
  }

  return stripe.createPaymentMethod(options).then((result) => {
    if (result.paymentMethod) {
      return result.paymentMethod
    } else if (result.error.type === 'validation_error') {
      // Stripe shows validation errors in the UI.
      throw new ValidationError()
    } else {
      // Handle other Stripe errors, such as test cards in live mode, and some declined scenarios;
      // Some automated tests use Stripe test cards in production.
      throw new TixStripeError(result.error)
    }
  })
}

function billingDetails(identity: Dictionary) {
  const enabledFields = new Set(config.stripe_billing_details_fields || [])

  function getValue(key: string) {
    const value = identity[key]
    if (enabledFields.has(key) && value && value.length > 0) {
      return value
    }
  }

  // @see https://stripe.com/docs/api/payment_methods/create#create_payment_method-billing_details
  const result: Dict<any> = {
    address: {} as Dict<string | undefined>,
  }

  result.name = getValue('name')
  result.email = getValue('email')
  result.phone = getValue('phone')
  result.address.postal_code = getValue('zip_code')
  result.address.state = getValue('state')
  result.address.city = getValue('city')
  result.address.line1 = getValue('address')
  result.address.country = getValue('country')

  return result
}

/**
 * Handles Stripe confirmation dialog for payments that require confirmation via a second factor. E.g. SMS confirmation
 * code or 3D Secure.
 */
export function confirmPayment(stripe: Stripe, clientSecret: string): Promise<string> {
  return stripe.confirmCardPayment(clientSecret).then((result) => {
    if (result.error) {
      throw new TixStripeError(result.error)
    } else {
      return result.paymentIntent.id
    }
  })
}
